import {PropsWithChildren} from "react";
import {createContext} from "../../../../../context/create-context";
import {FullPageLoader} from "../../../../../components/full-page-loader";
import {Button, Result} from "antd";
import {useParams} from "react-router-dom";
import {useLiveEvent} from "../../../../../features/live-events";
import type {LiveEvent} from "../../../../../features/live-events";
import {GetLiveEventApiResponse} from "../../../../../react-query-hooks/live-events/get-live-event/types";

/**
 * Adapts domain LiveEvent to legacy GetLiveEventApiResponse format.
 * This allows v2 API to be used while maintaining compatibility with existing consumers.
 */
function toLegacyFormat(event: LiveEvent): GetLiveEventApiResponse {
  return {
    id: event.id,
    userId: event.userId,
    name: event.name,
    slug: event.slug,
    description: event.description,
    topic_type: event.topicType,
    video_platform: event.videoPlatform as
      | "dailyCo"
      | "zoom"
      | ""
      | "googleMeet"
      | "physicalLocation",
    roomId: event.roomId,
    callPrice: event.callPrice,
    callPriceCurrency: event.callPriceCurrency,
    isPayWhatYouWant: event.isPayWhatYouWant,
    minCallPrice: event.minCallPrice,
    currencies: event.currencies.map((c) => ({
      id: c.id ?? 0,
      userId: event.userId,
      service_id: event.id,
      service_type: 3,
      currency: c.currency,
      amount: c.price,
      minCallPrice: c.minCallPrice,
      is_auto_converted: c.isAutoConverted ?? false,
      is_base:
        c.currency.toUpperCase() === event.callPriceCurrency.toUpperCase() ||
        c.isBase === true,
      createdAt: event.createdAt,
      updatedAt: event.updatedAt ?? event.createdAt,
    })),
    attendees: event.attendees,
    statusName: event.statusName as "Active" | "Paused",
    reference: event.reference,
    tiny_url: event.tinyUrl,
    cover_image: event.coverImage,
    cover_images: event.coverImages.map((img) => ({
      id: String(img.id),
      media_url: img.mediaUrl,
      media_type: "image" as const,
      offering_id: event.id,
      offering_type: "live event" as const,
      user_id: event.userId,
      createdAt: img.createdAt,
      updatedAt: img.createdAt,
    })),
    session_info: event.sessionInfo.map((s) => ({
      name: s.name,
      startDate: s.startDate,
      startTime: s.startTime,
      endTime: s.endTime,
      duration: s.duration,
    })),
    session_count: event.sessionCount,
    activeSession: event.activeSession
      ? {
          startDate: event.activeSession.startDate,
          startTime: event.activeSession.startTime,
          endTime: event.activeSession.endTime,
          duration: event.activeSession.duration,
        }
      : {startDate: "", startTime: "", endTime: "", duration: 0},
    activeSessionId: event.activeSessionId ?? 0,
    bookingQuestions: event.bookingQuestions.map((q) => ({
      question: q.question,
      answer_type: q.answerType as
        | "short-text"
        | "long-text"
        | "radio-button"
        | "checkbox"
        | "dropdown"
        | "link",
      required: q.required,
      options: q.options,
    })) as GetLiveEventApiResponse["bookingQuestions"],
    request_phone_number: event.requestPhoneNumber,
    purchaseLimit: event.purchaseLimit,
    hide_from_menu: event.hideFromMenu,
    url_redirect: event.urlRedirect,
    redirectType: event.redirectType,
    telegramGroupChatId: event.telegramGroupChatId,
    is_custom_room: event.isCustomRoom,
    hasGoogleMeet: event.hasGoogleMeet,
    location_address: event.locationAddress ?? null,
    location_lat: event.locationLat ?? null,
    location_lng: event.locationLng ?? null,
    is_location_public: event.isLocationPublic ?? true,
    enable_currency_conversion: event.enableCurrencyConversion,
    createdAt: event.createdAt,
    updatedAt: event.updatedAt ?? event.createdAt,
    statusId: 0,
    topics: ["Coachli"],
  };
}

type GetLiveEventState = {
  liveEventData: GetLiveEventApiResponse;
  isFetchingLiveEvent: boolean;
  isLoadingLiveEvent: boolean;
};

type GetLiveEventContextState = {
  state: GetLiveEventState;
};

const [useContext, ContextProvider] = createContext<GetLiveEventContextState>();

export const useGetLiveEventContext = useContext;

export const GetLiveEventProvider = ({children}: PropsWithChildren) => {
  const {id} = useParams();
  const getLiveEventQuery = useLiveEvent(Number(id));

  if (getLiveEventQuery.isLoading) {
    return <FullPageLoader />;
  }

  if (getLiveEventQuery.isError) {
    return (
      <Result
        status="500"
        title="Sorry, something went wrong."
        subTitle="Error fetching Event"
        extra={
          <Button type="primary" size="small" onClick={() => getLiveEventQuery.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }

  const state: GetLiveEventState = {
    liveEventData: toLegacyFormat(getLiveEventQuery.data!),
    isFetchingLiveEvent: getLiveEventQuery.isFetching,
    isLoadingLiveEvent: getLiveEventQuery.isLoading,
  };

  return <ContextProvider value={{state}}>{children}</ContextProvider>;
};
